import React from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, MinusCircle, Lightning, ArrowLeft } from "@phosphor-icons/react";
import { BrandLogo } from "../components/BrandLogo";
import useSeo from "../lib/useSeo";
import { formatPrice } from "../lib/pricingCurrency";
import useCurrency from "../lib/useCurrency";

// Head-to-head comparison pages built to rank for high-intent
// "{competitor} alternative" and "tradelog vs {competitor}" queries.
// Each competitor has its own preset — pricing, feature deltas, one-liner
// verdict — driving a single reusable page component under /vs/:slug.

const CONFIG = {
  tradezella: {
    name: "Tradezella",
    domain: "tradezella.com",
    priceLabel: "$29 / mo",
    tagline: "The polished trading journal for stocks and futures.",
    weakness: "Zero prop-firm rules engine · no MT statement import · no encrypted API auto-sync",
    metaTitle: "Tradelog vs Tradezella (2026) — the real comparison for prop-firm traders",
    metaDesc: "Tradelog vs Tradezella in 2026: prop-firm rules engine, MT4/MT5 statement import, encrypted broker auto-sync, discipline score, certificate PDF. Side-by-side.",
    verdict: "Tradezella is a beautiful stock-trader journal. If you're on a funded challenge — FTMO, FundedNext, MFF, MFTC — Tradezella can't enforce the rules. Tradelog can, and costs 4× less.",
    rows: [
      ["Paper prop-firm simulator (FTMO / FundedNext / MFF / MFTC rules)", "yes", "no"],
      ["Live daily-loss + max-drawdown enforcement", "yes", "no"],
      ["30% consistency rule check at payout day", "yes", "no"],
      ["High-impact news-trading lock", "yes", "no"],
      ["Certificate PDF on challenge PASS (QR verified)", "yes", "no"],
      ["MT4 / MT5 HTML statement import (XM, Vantage, IC, Pepperstone, Exness…)", "yes", "no"],
      ["Encrypted broker auto-sync (Binance / CoinDCX / Delta)", "yes", "no"],
      ["MCX Gold / Silver / Crude live charts", "yes", "no"],
      ["Broker CSV import (Zerodha / Upstox / Groww / Angel One / Fyers)", "yes", "partial"],
      ["Discipline / rule-break score", "yes", "no"],
      ["Public P&L share card + QR", "yes", "yes"],
      ["TradingView-powered charts inside", "yes", "partial"],
      ["Backtester with replay", "partial", "yes"],
      ["Playbook / tag library", "yes", "yes"],
      ["Free trial, no card", "14 days", "7 days"],
      ["Monthly price", "₹600 (~$7)", "$29"],
    ],
    faqs: [
      { q: "Is Tradelog a Tradezella alternative?", a: "Yes — Tradelog covers everything Tradezella does for trade logging, and adds a full paper prop-firm challenge simulator, MT4/MT5 statement import, encrypted broker auto-sync, and a QR-verified PASS certificate. It's the drop-in Tradezella replacement for anyone grinding a funded evaluation." },
      { q: "How much cheaper is Tradelog than Tradezella?", a: "Tradelog is about 4× cheaper — ₹600 (~$7) / mo vs $29 / mo for Tradezella. Both charge no per-trade fees. Tradelog's yearly plan works out to under $4 / mo." },
      { q: "Can I migrate my Tradezella trades to Tradelog?", a: "Yes. Export your Tradezella trades as CSV (Tradezella supports this in Settings → Export), then upload the CSV to Tradelog via Trades → Import. Tradelog auto-maps every column and dedupes on ticket ID." },
    ],
  },
  edgewonk: {
    name: "Edgewonk",
    domain: "edgewonk.com",
    priceLabel: "$169 / year",
    tagline: "The stat-heavy classic desktop journal — great in 2018, aging in 2026.",
    weakness: "No prop-firm simulator · no auto-sync · single upfront payment · dated UI",
    metaTitle: "Tradelog vs Edgewonk (2026) — is Edgewonk still worth $169 in 2026?",
    metaDesc: "Tradelog vs Edgewonk 2.0 in 2026: prop-firm rules engine, MT4/MT5 import, mobile-first UI, encrypted auto-sync. Do you still need Edgewonk?",
    verdict: "Edgewonk 2.0 is a stat-heavy classic that traders still love for its tag depth. But it hasn't shipped a prop-firm simulator, mobile PWA, or encrypted broker sync — and 2026 traders live on all three. Tradelog matches Edgewonk's analytics and adds what modern prop-firm traders need.",
    rows: [
      ["Paper prop-firm simulator (FTMO / FundedNext / MFF / MFTC rules)", "yes", "no"],
      ["Live daily-loss + max-drawdown enforcement", "yes", "no"],
      ["30% consistency rule check at payout day", "yes", "no"],
      ["High-impact news-trading lock", "yes", "no"],
      ["Certificate PDF on challenge PASS (QR verified)", "yes", "no"],
      ["MT4 / MT5 HTML statement import (XM, Vantage, IC, Pepperstone, Exness…)", "yes", "yes"],
      ["Encrypted broker auto-sync (Binance / CoinDCX / Delta)", "yes", "no"],
      ["MCX Gold / Silver / Crude live charts", "yes", "no"],
      ["Broker CSV import (Zerodha / Upstox / Groww / Angel One / Fyers)", "yes", "partial"],
      ["Discipline / rule-break score", "yes", "yes"],
      ["Public P&L share card + QR", "yes", "no"],
      ["TradingView-powered charts inside", "yes", "no"],
      ["Backtester with replay", "partial", "yes"],
      ["Playbook / tag library", "yes", "yes"],
      ["Mobile-first UI + PWA", "yes", "no"],
      ["Pricing model", "₹600 (~$7) / mo · cancel anytime", "$169 / yr · single upfront"],
    ],
    faqs: [
      { q: "Is Tradelog a real Edgewonk 2.0 alternative?", a: "Yes — Tradelog matches Edgewonk's tag depth, statistics and MT statement import, and adds a prop-firm simulator, encrypted broker auto-sync, live TradingView charts inside the app, and a mobile-first UI. Modern prop-firm traders get more for less." },
      { q: "Is Edgewonk still worth it in 2026?", a: "Only if you're a stock/futures trader who lives inside spreadsheets and doesn't do funded challenges. For anyone on FTMO, FundedNext, MFF or an MFTC-style evaluation, Edgewonk's lack of a prop-firm rules engine is a dealbreaker." },
      { q: "Does Tradelog have Edgewonk's Trade Tools?", a: "Tradelog ships equivalents of Edgewonk's core Trade Tools — R-multiple analytics, timing heatmap, session breakdown, tag-level P&L — plus a rules engine Edgewonk doesn't have. Playbook and MFE/MAE are on the P1 roadmap for Q2 2026." },
    ],
  },
};

const SITE = "https://thetradlog.com";

function Cell({ v }) {
  if (v === "yes") return <CheckCircle size={20} weight="fill" className="inline-block text-emerald-400" />;
  if (v === "no")  return <XCircle    size={20} weight="fill" className="inline-block text-red-400 opacity-70" />;
  if (v === "partial") return <MinusCircle size={20} weight="fill" className="inline-block text-amber-400" />;
  return <span className="text-neutral-300 text-sm">{v}</span>;
}

export default function VsPage() {
  const { slug } = useParams();
  const cfg = CONFIG[slug];
  const [ccy] = useCurrency();
  useSeo({
    title: cfg ? cfg.metaTitle : "Compare · Tradelog",
    description: cfg ? cfg.metaDesc : undefined,
    canonical: `/vs/${slug}`,
    jsonLd: cfg ? [
      {
        "@context": "https://schema.org", "@type": "Article",
        headline: cfg.metaTitle,
        description: cfg.metaDesc,
        image: [SITE + "/img/og-cover.png"],
        datePublished: "2026-02-18T09:00:00+00:00",
        dateModified: "2026-02-18T09:00:00+00:00",
        author: { "@type": "Person", name: "Yash Khajania", url: SITE },
        publisher: { "@type": "Organization", name: "Tradelog", url: SITE, logo: { "@type": "ImageObject", url: SITE + "/img/og-cover.png" } },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/vs/${slug}` },
        inLanguage: "en",
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: cfg.faqs.map(f => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home",    item: SITE + "/" },
          { "@type": "ListItem", position: 2, name: "Compare", item: SITE + "/vs" },
          { "@type": "ListItem", position: 3, name: `Tradelog vs ${cfg.name}`, item: `${SITE}/vs/${slug}` },
        ],
      },
    ] : undefined,
  });
  if (!cfg) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white p-10">
        <p>Comparison not found. <Link to="/" className="text-primary underline">Back home</Link>.</p>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid={`vs-page-${slug}`}>
      <header className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="vs-brand">
          <BrandLogo size={20} wordmark />
        </Link>
        <Link to="/signup">
          <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="vs-cta-signup">Start free trial</Button>
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 pb-24">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-neutral-400 hover:text-white text-xs">
            <ArrowLeft size={12} /> back to Tradelog
          </Link>
          <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-primary/80 font-semibold">Head to head · 2026</p>
          <h1 className="mt-2 font-display font-black text-3xl sm:text-5xl leading-tight tracking-tight">
            Tradelog vs <span className="text-primary">{cfg.name}</span>
          </h1>
          <p className="mt-4 text-neutral-400 text-base sm:text-lg max-w-2xl mx-auto">{cfg.tagline}</p>
        </div>

        {/* Verdict card */}
        <div className="mt-10 rounded-2xl border border-primary/30 bg-primary/5 p-6" data-testid="vs-verdict">
          <p className="text-[10px] uppercase tracking-[0.3em] text-primary/80 font-bold flex items-center gap-1.5">
            <Lightning size={12} weight="fill" /> Verdict
          </p>
          <p className="mt-2 font-display text-xl sm:text-2xl leading-snug">{cfg.verdict}</p>
          <p className="mt-4 text-sm text-neutral-400">
            Tradelog {formatPrice(600, ccy)} / mo &nbsp;·&nbsp; {cfg.name} {cfg.priceLabel} &nbsp;·&nbsp; Same feature bar, half the price, twice the prop-firm depth.
          </p>
        </div>

        {/* Compare table */}
        <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
          <table className="w-full text-sm" data-testid="vs-compare-table">
            <thead>
              <tr className="text-left border-b border-white/10">
                <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-semibold">Feature</th>
                <th className="px-4 py-4 text-center"
                    style={{ background: "linear-gradient(180deg, rgba(59,130,246,0.18), rgba(59,130,246,0.05))", borderTop: "2px solid #5aa2ff" }}>
                  <div className="flex items-center justify-center gap-1.5">
                    <Lightning size={12} weight="fill" className="text-primary" />
                    <span className="font-display font-bold text-white">Tradelog</span>
                  </div>
                  <div className="text-[10px] mt-1 font-semibold text-primary">{formatPrice(600, ccy)} / mo</div>
                </th>
                <th className="px-4 py-4 text-center">
                  <p className="font-semibold text-white">{cfg.name}</p>
                  <p className="text-[10px] text-neutral-500 mt-1">{cfg.priceLabel}</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {cfg.rows.map((r, i) => (
                <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
                  <td className="px-5 py-3.5 text-neutral-200">{r[0]}</td>
                  <td className="px-4 py-3.5 text-center bg-primary/5"><Cell v={r[1]} /></td>
                  <td className="px-4 py-3.5 text-center"><Cell v={r[2]} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Weakness callout */}
        <div className="mt-8 rounded-xl border border-red-500/20 bg-red-500/5 p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-bold">What {cfg.name} doesn't do</p>
          <p className="mt-2 text-neutral-200 text-sm sm:text-base">{cfg.weakness}</p>
        </div>

        {/* FAQ */}
        <div className="mt-14">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Questions we get about switching from {cfg.name}</h2>
          <div className="mt-6 grid gap-3">
            {cfg.faqs.map((f, i) => (
              <details key={i} className="group rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid={`vs-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="font-display font-bold text-white">{f.q}</span>
                  <span className="text-neutral-500 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-neutral-300 text-sm sm:text-base leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-indigo-500/5 p-8 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-primary/80 font-bold">Try Tradelog free</p>
          <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold">Same feature bar as {cfg.name}. Half the price. Twice the prop-firm depth.</h3>
          <p className="mt-3 text-neutral-400">14-day free trial. No card. Cancel with one click.</p>
          <Link to="/signup" className="inline-block mt-6">
            <Button className="rounded-full bg-primary hover:bg-primary/90 h-11 px-8" data-testid="vs-final-cta">
              Start free trial
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
